import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetches the backend-computed production size estimate for an existing
 * order on the Quote Calculator.
 *
 * Used in the "Go Back & Resize" edit flow: when a customer comes back
 * to /quote from /upload-artwork with a known orderId, we surface the
 * auto-adjusted production dimensions directly in the live quote card
 * (alongside the requested W × H) so the customer sees what we'll
 * actually print before they commit.
 *
 * Re-fetches whenever (orderId, width, height, unit) changes — so a
 * customer fiddling with the dimensions sees the production estimate
 * update live. Silent no-op (returns null) when there's no orderId.
 */
export const useProductionEstimate = ({ orderId, width, height, unit }) => {
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setEstimate(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/enhanced-orders/${orderId}/production-size-estimate`,
        );
        if (!cancelled) setEstimate(data);
      } catch {
        if (!cancelled) setEstimate(null);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, width, height, unit]);

  return estimate;
};

export default useProductionEstimate;
